/**
 * Utility functions for sorting filters with priority rules
 */

import { FilterUsageStorage } from './filterUsageStorage'

export interface SortableFilter {
  id: string
  label: string
  isFavorite?: boolean
  isUserCreated?: boolean
  lastUsed?: number
}

/**
 * Sort filters with the following priority:
 * 1. Active filter (if selectedFilterId matches) - always first
 * 2. Favorites - sorted by most recently used
 * 3. Recent filters (used within last 7 days) - sorted by most recently used
 * 4. Others - sorted alphabetically
 */
export const sortFilters = (
  filters: SortableFilter[],
  selectedFilterId?: string,
): SortableFilter[] => {
  return [...filters].sort((a, b) => {
    // 1. Active filter always on top
    if (a.id === selectedFilterId) return -1
    if (b.id === selectedFilterId) return 1

    // 2. Favorites come next
    if (a.isFavorite !== b.isFavorite) {
      return a.isFavorite ? -1 : 1
    }

    // 3. Recent filters (within favorites or non-favorites)
    const aIsRecent = FilterUsageStorage.isRecent(a.lastUsed)
    const bIsRecent = FilterUsageStorage.isRecent(b.lastUsed)

    if (aIsRecent !== bIsRecent) {
      return aIsRecent ? -1 : 1
    }

    // 3a. Within recent filters, sort by most recently used
    if (aIsRecent && bIsRecent) {
      return (b.lastUsed || 0) - (a.lastUsed || 0)
    }

    // 4. Others alphabetically
    return (a.label ?? '').localeCompare(b.label ?? '')
  })
}

/**
 * Filter list by search term (case-insensitive)
 */
export const filterBySearch = (
  filters: SortableFilter[],
  searchTerm: string,
): SortableFilter[] => {
  if (!searchTerm.trim()) return filters

  const lowerSearch = searchTerm.toLowerCase()
  return filters.filter((filter) =>
    (filter.label ?? '').toLowerCase().includes(lowerSearch),
  )
}

/**
 * Separate filters into categories for organized display
 */
export const categorizeFilters = (
  filters: SortableFilter[],
  entityName: string,
) => {
  const filterUsage = FilterUsageStorage.load(entityName)

  const favorites: SortableFilter[] = []
  const recent: SortableFilter[] = []
  const others: SortableFilter[] = []

  filters.forEach((filter) => {
    const lastUsed = filterUsage[filter.id]
    const isRecent = FilterUsageStorage.isRecent(lastUsed)

    if (filter.isFavorite) {
      favorites.push({ ...filter, lastUsed })
    } else if (isRecent) {
      recent.push({ ...filter, lastUsed })
    } else {
      others.push({ ...filter, lastUsed })
    }
  })

  // Sort each category
  favorites.sort((a, b) => (b.lastUsed || 0) - (a.lastUsed || 0))
  recent.sort((a, b) => (b.lastUsed || 0) - (a.lastUsed || 0))
  others.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''))

  return { favorites, recent, others }
}
